import axiosCustom from "../utils/axiosInstance";
import { ChatMessageType } from "../models/chat/ChatMessageDto";
import {
  ChatGroupDetailDto,
  ChatGroupListDto,
  ChatGroupMemberRole,
  ChatGroupMessageDto,
  CreateChatGroupRequest,
} from "../models/chat/ChatGroup";

const API_BASE_URL = "/chat/groups";

export const chatGroupService = {
  listMyGroups: async (): Promise<ChatGroupListDto[]> => {
    const res = await axiosCustom.get<ChatGroupListDto[]>(`${API_BASE_URL}`);
    return res.data;
  },
  getGroupDetail: async (groupId: string): Promise<ChatGroupDetailDto> => {
    const res = await axiosCustom.get<ChatGroupDetailDto>(`${API_BASE_URL}/${groupId}`);
    return res.data;
  },
  getGroupMessages: async (groupId: string, before?: string, size = 30): Promise<ChatGroupMessageDto[]> => {
    const res = await axiosCustom.get<ChatGroupMessageDto[]>(`${API_BASE_URL}/${groupId}/messages`, {
      params: { before, size },
    });
    return res.data;
  },
  sendGroupMessage: async (groupId: string, content: string, messageType: ChatMessageType = "TEXT"): Promise<ChatGroupMessageDto> => {
    const res = await axiosCustom.post<ChatGroupMessageDto>(`${API_BASE_URL}/${groupId}/messages`, {
      content,
      messageType,
    });
    return res.data;
  },
  sendGroupAttachment: async (groupId: string, files: File[], content?: string): Promise<ChatGroupMessageDto> => {
    const form = new FormData();
    if (content) form.append("content", content);
    files.forEach((f) => form.append("files", f));
    const res = await axiosCustom.post<ChatGroupMessageDto>(`${API_BASE_URL}/${groupId}/messages/attachment`, form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return res.data;
  },
  downloadGroupFile: async (groupId: string, fileId: string, fileName: string): Promise<void> => {
    const res = await axiosCustom.get(`${API_BASE_URL}/${groupId}/files/${fileId}/download`, { responseType: "blob" });
    const url = window.URL.createObjectURL(res.data as Blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },
  markGroupRead: async (groupId: string): Promise<void> => {
    await axiosCustom.patch(`${API_BASE_URL}/${groupId}/read`);
  },
  createGroup: async (request: CreateChatGroupRequest): Promise<ChatGroupDetailDto> => {
    const res = await axiosCustom.post<ChatGroupDetailDto>(`${API_BASE_URL}`, request);
    return res.data;
  },
  renameGroup: async (groupId: string, name: string): Promise<ChatGroupDetailDto> => {
    const res = await axiosCustom.patch<ChatGroupDetailDto>(`${API_BASE_URL}/${groupId}`, { name });
    return res.data;
  },
  updateGroupAvatar: async (groupId: string, file: File): Promise<ChatGroupDetailDto> => {
    const form = new FormData();
    form.append("file", file);
    const res = await axiosCustom.post<ChatGroupDetailDto>(`${API_BASE_URL}/${groupId}/avatar`, form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return res.data;
  },
  addGroupMembers: async (groupId: string, userIds: string[]): Promise<ChatGroupDetailDto> => {
    const res = await axiosCustom.post<ChatGroupDetailDto>(`${API_BASE_URL}/${groupId}/members`, { userIds });
    return res.data;
  },
  removeGroupMember: async (groupId: string, targetUserId: string): Promise<void> => {
    await axiosCustom.delete(`${API_BASE_URL}/${groupId}/members/${targetUserId}`);
  },
  changeGroupMemberRole: async (groupId: string, targetUserId: string, role: ChatGroupMemberRole): Promise<void> => {
    await axiosCustom.patch(`${API_BASE_URL}/${groupId}/members/${targetUserId}/role`, null, { params: { role } });
  },
  leaveGroup: async (groupId: string): Promise<void> => {
    await axiosCustom.post(`${API_BASE_URL}/${groupId}/leave`);
  },
  disbandGroup: async (groupId: string): Promise<void> => {
    await axiosCustom.delete(`${API_BASE_URL}/${groupId}`);
  },
};
